import { useState, useEffect, Children, isValidElement, cloneElement } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import TX, { useTX } from "../components/TX";

/**
 * Recursively walk a React tree and wrap every plain-string child node in
 * `<TX>`. This lets long-form static pages (Policies, FAQ, About, etc.) get
 * full translation coverage without hand-editing every `<p>` and `<h3>`.
 *
 * Skipped:
 *   • Numbers, null, undefined, booleans
 *   • Elements that already contain a `<TX>` child or carry `data-no-tx`
 *   • Non-renderable types (functions, etc.)
 */
const translateTree = (node) => {
  if (typeof node === "string") {
    const trimmed = node.trim();
    // Skip whitespace-only and pure-punctuation/numeric snippets — translating
    // them is a wasted round-trip and risks the model "correcting" them.
    if (!trimmed || /^[\s\W\d]+$/.test(trimmed)) return node;
    return <TX>{node}</TX>;
  }
  if (Array.isArray(node)) return node.map((c, i) => <TXFrag key={i}>{c}</TXFrag>);
  if (!isValidElement(node)) return node;
  if (node.props?.["data-no-tx"]) return node;
  const kids = node.props?.children;
  if (kids === undefined || kids === null) return node;
  const translatedKids = Array.isArray(kids)
    ? Children.map(kids, translateTree)
    : translateTree(kids);
  return cloneElement(node, {}, translatedKids);
};

// Small wrapper so we can use translateTree on top-level arrays / fragments.
const TXFrag = ({ children }) => <>{translateTree(children)}</>;

export const SimplePage = ({ id, eyebrow, title, lede, children, image }) => (
  <div data-testid={`page-${id}`} className="pt-32">
    {image && (
      <div className="relative h-[60vh] overflow-hidden mb-16">
        <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black"></div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 h-full flex flex-col justify-end pb-16">
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5"><TX>{eyebrow}</TX></p>
          <h1 className="font-heading font-light text-5xl md:text-7xl leading-[1.05]"><TX>{title}</TX></h1>
        </div>
      </div>
    )}
    {!image && (
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-12">
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5"><TX>{eyebrow}</TX></p>
        <h1 className="font-heading font-light text-5xl md:text-7xl leading-[1.05]"><TX>{title}</TX></h1>
      </div>
    )}
    <div className="max-w-3xl mx-auto px-6 pb-32 text-white/70 leading-relaxed text-lg space-y-6">
      {lede && <p className="text-2xl text-ivory font-heading leading-snug"><TX>{lede}</TX></p>}
      <TXFrag>{children}</TXFrag>
    </div>
  </div>
);

const useInquiry = (type) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inquiries", { type, ...form });
      setSent(true); toast.success("Submitted. Concierge will respond within 24 hours.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return { form, setForm, sent, submit };
};

const TXInput = (props) => {
  const { placeholder, ...rest } = props;
  const t = useTX();
  return <input {...rest} placeholder={t(placeholder)} />;
};

const TXTextarea = (props) => {
  const { placeholder, ...rest } = props;
  const t = useTX();
  return <textarea {...rest} placeholder={t(placeholder)} />;
};

const ContactForm = ({ type = "contact", testid }) => {
  const { form, setForm, sent, submit } = useInquiry(type);
  if (sent) return <div data-testid={`${testid}-success`} className="bg-onyx border border-gold/40 p-8 text-center"><div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-3"><TX>Submitted</TX></div><div className="text-white/70"><TX>We will be in touch within 24 hours.</TX></div></div>;
  return (
    <form onSubmit={submit} className="space-y-5 bg-onyx border border-white/10 p-8">
      <TXInput data-testid={`${testid}-name`} required placeholder="Full Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
      <TXInput data-testid={`${testid}-email`} required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
      <TXInput data-testid={`${testid}-phone`} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
      <TXTextarea data-testid={`${testid}-msg`} required rows={4} placeholder="Message" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="bg-transparent border border-white/20 px-3 py-3 w-full focus:outline-none focus:border-gold" />
      <button data-testid={`${testid}-submit`} className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-ivory"><TX>Send</TX></button>
    </form>
  );
};

export const About = () => (
  <SimplePage id="about" eyebrow="Heritage" title="The Maison." image="https://images.pexels.com/photos/33257666/pexels-photo-33257666.jpeg"
    lede="OPIKJEWELS is a Southeast Asian luxury gemstone house — a private dealer, an auction house, and an investment portfolio for collectors of singular taste. Headquartered in Jakarta with a trading office in Bangkok.">
    <p>Founded in Jakarta and rooted in three decades of gemological expertise, OPIKJEWELS is a Southeast Asian luxury gemstone house — curating the rarest specimens from the world's most legendary mines: Mogok, Kashmir, Muzo, Golconda, Argyle.</p>
    <p>Our atelier serves royal houses, family offices, and discerning collectors from Jakarta and Bangkok across Singapore, Dubai, New York, and London. Every stone we offer is hand-selected, laboratory-certified, and accompanied by lifetime provenance.</p>
    <div className="gold-line w-32 my-12"></div>
    <p className="font-heading text-3xl text-ivory leading-snug italic">"A gemstone is not bought. It is inherited from the earth."</p>
    <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mt-4">— Founding Principle</p>
  </SimplePage>
);

export const PrivateSales = () => (
  <SimplePage id="private-sales" eyebrow="Private Sales" title="Discreet acquisition." image="https://images.unsplash.com/photo-1735480166632-38176b26f212"
    lede="Beyond the auction floor, OPIKJEWELS facilitates the private acquisition of investment-grade gemstones for collectors who require absolute discretion.">
    <p>Our private sales department serves clients seeking specific provenance, color, or origin — including no-heat Kashmir sapphires, Type IIa diamonds, Mogok pigeon-blood rubies, and Argyle pink reserves.</p>
    <ContactForm type="private_sale" testid="private-sales" />
  </SimplePage>
);

export const SellGemstone = () => (
  <SimplePage id="sell" eyebrow="Sell Your Gemstone" title="Consign with confidence." image="https://images.unsplash.com/photo-1763256614634-7feb3ff79ff3"
    lede="Submit your stone for evaluation by our gemologists. We provide complimentary appraisals, transparent valuations, and global market access.">
    <p>OPIKJEWELS represents sellers in private sales and at auction. Our reach extends from Jakarta and Bangkok to Singapore, Dubai, and New York. Reserve prices honored. Discretion absolute.</p>
    <ContactForm type="sell_gemstone" testid="sell" />
  </SimplePage>
);

export const Concierge = () => (
  <SimplePage id="concierge" eyebrow="Concierge" title="Your private gemologist." image="https://images.unsplash.com/photo-1762019313711-8b5d1e4f7ba4"
    lede="A senior consultant at your service — for sourcing, appraisal, secure shipping, insurance, and lifetime aftercare.">
    <p>Our concierge is reachable by appointment. Jakarta · Bangkok · Singapore · Dubai · private travel available worldwide.</p>
    <ContactForm type="concierge" testid="concierge" />
  </SimplePage>
);

export const InvestorRelations = () => (
  <SimplePage id="investor" eyebrow="Investor Relations" title="Gemstones as assets." image="https://images.pexels.com/photos/21235148/pexels-photo-21235148.jpeg"
    lede="Investment-grade gemstones have outperformed equities and gold over the past two decades. OPIKJEWELS curates portfolios for family offices and private wealth.">
    <div className="grid grid-cols-2 gap-8 my-12">
      {[["+18% CAGR", "Pink Diamonds (10y)"], ["+12% CAGR", "Kashmir Sapphires (10y)"], ["+9% CAGR", "Burmese Rubies (10y)"], ["+7% CAGR", "Muzo Emeralds (10y)"]].map(([v, l]) => (
        <div key={l} className="border-l border-gold pl-6">
          <div className="font-heading text-4xl text-ivory">{v}</div>
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mt-2">{l}</div>
        </div>
      ))}
    </div>
    <p>Past performance does not guarantee future results. All allocations are bespoke and underwritten by independent laboratory grading.</p>
  </SimplePage>
);

const POSTS_FALLBACK = [
  { title: "The Vanishing Argyle", excerpt: "With the closure of the Argyle mine, fancy vivid pinks have entered an era of true scarcity.", image_url: "https://images.unsplash.com/photo-1705575420317-daaed2ab4cf3", slug: "vanishing-argyle" },
  { title: "Kashmir, Reawakened", excerpt: "After a century of silence, are new Kashmir sapphires possible? Our gemologists weigh in.", image_url: "https://images.unsplash.com/photo-1762019313711-8b5d1e4f7ba4", slug: "kashmir-reawakened" },
  { title: "Why Provenance Outperforms Carat", excerpt: "The market rewards origin. A 4ct Mogok ruby commands a multiple over a 6ct heated stone.", image_url: "https://images.unsplash.com/photo-1777126413608-f06fbd6e3208", slug: "provenance-vs-carat" },
];

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [openPost, setOpenPost] = useState(null);

  useEffect(() => {
    api.get("/blog").then(({ data }) => {
      setPosts(data.length ? data : POSTS_FALLBACK);
    }).catch(() => setPosts(POSTS_FALLBACK));
  }, []);

  const open = async (p) => {
    if (p.body) { setOpenPost(p); return; }
    if (!p.slug || POSTS_FALLBACK.find((f) => f.slug === p.slug)) {
      setOpenPost({ ...p, body: p.excerpt });
      return;
    }
    try {
      const { data } = await api.get(`/blog/${p.slug}`);
      setOpenPost(data);
    } catch {
      setOpenPost({ ...p, body: p.excerpt });
    }
  };

  return (
    <div data-testid="blog-page" className="pt-32">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-12">
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Market Insight</p>
        <h1 className="font-heading font-light text-5xl md:text-7xl">Journal.</h1>
      </section>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-3 gap-8 pb-32">
        {posts.map((p) => (
          <article key={p.slug || p.title} data-testid={`blog-card-${p.slug || p.title}`} onClick={() => open(p)} className="bg-onyx border border-white/10 hover:border-gold/40 transition-all duration-700 group cursor-pointer">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={p.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="p-8">
              <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">
                {p.category ? p.category.replace("_", " ") : "Market Insight"}{p.ai_generated ? " · Editorial" : ""}
              </p>
              <h3 className="font-heading text-2xl mb-3 leading-tight">{p.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{p.excerpt}</p>
            </div>
          </article>
        ))}
      </section>

      {openPost && (
        <div data-testid="blog-modal" className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={() => setOpenPost(null)}>
          <div className="max-w-3xl mx-auto my-16 bg-noir border border-gold/30 p-8 md:p-12" onClick={(e) => e.stopPropagation()}>
            {openPost.image_url && <div className="aspect-[16/9] overflow-hidden mb-8"><img src={openPost.image_url} alt="" className="w-full h-full object-cover" /></div>}
            <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-4">{openPost.category?.replace("_", " ") || "Market Insight"}</p>
            <h2 className="font-heading text-4xl md:text-5xl leading-tight mb-6">{openPost.title}</h2>
            {openPost.published_at && <div className="text-white/40 text-xs font-accent tracking-[0.2em] uppercase mb-8">{new Date(openPost.published_at).toLocaleDateString()} · {openPost.author || "OPIKJEWELS"}</div>}
            <div className="text-white/80 leading-relaxed text-lg whitespace-pre-wrap font-light">{openPost.body}</div>
            <button data-testid="blog-modal-close" onClick={() => setOpenPost(null)} className="mt-10 border border-white/20 text-white/70 font-accent uppercase tracking-[0.28em] text-xs px-7 py-3 hover:border-gold hover:text-gold">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const Contact = () => (
  <SimplePage id="contact" eyebrow="Contact" title="Connect with the Maison.">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
      {[["Jakarta · Headquarters", "Jakarta Gems Center, Ground Floor, Block BKS 48-49-50-51 (Pintu Bacan), Jakarta 13320, Indonesia", "+62 877 8105 8814"], ["Bangkok · Trading Office", "Silom Road, Bang Rak, Bangkok 10500, Thailand", "+62 877 8105 8814"]].map(([c, a, p]) => (
        <div key={c} className="border-l border-gold pl-6">
          <div className="font-heading text-2xl text-ivory">{c}</div>
          <div className="text-white/50 text-sm mt-2">{a}</div>
          <div className="text-white/70 text-sm mt-1">{p}</div>
        </div>
      ))}
    </div>
    <ContactForm type="contact" testid="contact" />
  </SimplePage>
);

export const InvestmentGems = () => (
  <SimplePage id="investment" eyebrow="Investment Gems" title="Allocations of singular merit." image="https://images.unsplash.com/photo-1735480166632-38176b26f212"
    lede="Curated portfolios of investment-grade gemstones — designed for capital preservation, generational transfer, and independence from public markets.">
    <p>OPIKJEWELS partners with family offices and private banks to construct gemstone allocations across diamonds, sapphires, rubies, emeralds, and rare stones. Every portfolio is independently certified, fully insured, and stored in tier-one vaults.</p>
    <ContactForm type="private_sale" testid="investment" />
  </SimplePage>
);
